import { useEffect, useRef, useState, type DragEvent } from 'react'
import { uploadFile } from '../../lib/files.js'
import { Button } from '../ui.js'

type Failure = { name: string; reason: string }

type Status =
  | { state: 'idle' }
  | { state: 'uploading'; done: number; total: number }
  | { state: 'finished'; attached: number; failures: Failure[] }

/**
 * The whole job card as a drop target for files.
 *
 * Each file goes through `POST /files` with the job's id, one after another, so
 * a rejected one (wrong type, over the limit) is named on its own and does not
 * take the rest of the batch with it. The server is the authority on what is
 * accepted; the only thing refused here is a folder, which arrives as a `File`
 * with no type and would otherwise read as "application/octet-stream".
 *
 * A drop counts only when its target is in the card's own DOM. React bubbles
 * events out of portals, so without that check a file dropped anywhere in the
 * proposal builder or a booking popup would also land on the job. A nested
 * zone that wants the file — a proposal photo block — claims it with
 * `preventDefault`, and the card stands down.
 */
export function useFileDrop({
  jobId,
  enabled,
  onUploaded,
}: {
  jobId: string
  enabled: boolean
  onUploaded: () => void
}) {
  const [over, setOver] = useState(false)
  const [status, setStatus] = useState<Status>({ state: 'idle' })
  const busy = status.state === 'uploading'

  const claims = (event: DragEvent<HTMLElement>) =>
    enabled &&
    !busy &&
    event.dataTransfer.types.includes('Files') &&
    !event.defaultPrevented &&
    event.currentTarget.contains(event.target as Node)

  const onDragOver = (event: DragEvent<HTMLElement>) => {
    if (!claims(event)) {
      if (over) setOver(false)
      return
    }
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
    if (!over) setOver(true)
  }

  const onDragLeave = (event: DragEvent<HTMLElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOver(false)
  }

  const onDrop = (event: DragEvent<HTMLElement>) => {
    setOver(false)
    if (!claims(event)) return
    event.preventDefault()
    void upload(Array.from(event.dataTransfer.files))
  }

  async function upload(files: File[]) {
    if (files.length === 0) return

    const failures: Failure[] = []
    let attached = 0
    setStatus({ state: 'uploading', done: 0, total: files.length })

    for (const [index, file] of files.entries()) {
      if (!file.type) {
        failures.push({ name: file.name, reason: 'Folders and unknown file types cannot be attached.' })
      } else {
        try {
          await uploadFile(file, { jobId })
          attached += 1
        } catch (error) {
          failures.push({
            name: file.name,
            reason: error instanceof Error ? error.message : 'That file did not upload.',
          })
        }
      }
      setStatus({ state: 'uploading', done: index + 1, total: files.length })
    }

    if (attached > 0) onUploaded()
    setStatus({ state: 'finished', attached, failures })
  }

  return {
    over,
    status,
    dismiss: () => setStatus({ state: 'idle' }),
    handlers: { onDragOver, onDragEnter: onDragOver, onDragLeave, onDrop },
  }
}

/** The veil over the card while a file is held above it. */
export function DropVeil({ label }: { label: string }) {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-2 z-30 flex flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-brand-600 bg-white/90"
    >
      <span className="text-[15px] font-semibold text-ink-900">Drop to attach to {label}</span>
      <span className="text-[12.5px] text-ink-500">
        Photos, PDFs, video or audio · up to 25 MB each
      </span>
    </div>
  )
}

/**
 * What happened to the last drop.
 *
 * A clean batch says so and goes away on its own; a batch with a refusal in it
 * stays until dismissed, because the names of the files that did not go are the
 * part somebody has to act on.
 */
export function DropStatus({
  status,
  onDismiss,
  onShowFiles,
}: {
  status: Status
  onDismiss: () => void
  onShowFiles: () => void
}) {
  const dismiss = useRef(onDismiss)
  dismiss.current = onDismiss

  const clean = status.state === 'finished' && status.failures.length === 0

  useEffect(() => {
    if (!clean) return
    const timer = window.setTimeout(() => dismiss.current(), 5000)
    return () => window.clearTimeout(timer)
  }, [clean, status])

  if (status.state === 'idle') return null

  const shell =
    'absolute bottom-4 left-1/2 z-30 flex w-[min(420px,calc(100%-2rem))] -translate-x-1/2 flex-col gap-1.5 rounded-lg bg-ink-900 px-3.5 py-2.5 text-[13px] text-white shadow-lg'

  if (status.state === 'uploading') {
    return (
      <div role="status" className={shell}>
        Attaching {Math.min(status.done + 1, status.total)} of {status.total}…
      </div>
    )
  }

  const { attached, failures } = status

  return (
    <div role={failures.length > 0 ? 'alert' : 'status'} className={shell}>
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1">
          {attached > 0
            ? `Attached ${attached} ${attached === 1 ? 'file' : 'files'}.`
            : 'Nothing was attached.'}
        </span>
        {attached > 0 ? (
          <Button
            size="sm"
            variant="ghost"
            className="text-white hover:bg-white/10 hover:text-white"
            onClick={() => {
              onShowFiles()
              onDismiss()
            }}
          >
            Show
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="ghost"
          className="text-white hover:bg-white/10 hover:text-white"
          onClick={onDismiss}
        >
          Dismiss
        </Button>
      </div>
      {failures.length > 0 ? (
        <ul className="flex flex-col gap-0.5 text-[12.5px] text-red-200">
          {failures.map((failure, index) => (
            <li key={`${failure.name}:${index}`}>
              <span className="font-semibold">{failure.name}</span> — {failure.reason}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
